import { useState, useEffect, useMemo, useCallback } from 'react';
import { Select } from '@tencent/tea-component';
import { usePortalStore } from '@src/store';
import { useLocation } from 'react-router-dom';
import { AddCircleIcon } from '@tencent/tpm-audio-video-react-icons';
import { parseApp } from '@src/utils/parser';

// 店铺选项类型
export interface MerchantOption {
  value: string;
  text: string;
  isDefault?: boolean;
  [key: string]: any;
}

// 扩展菜单类型，支持 hideTopMerchantList 属性
interface ExtendedMenu extends Application.Menu {
  hideTopMerchantList?: boolean;
}

// 彩色日志打印
const logEvent = (eventName: string, data: unknown) => {
  console.log(
    '%c[AppSelectPlanB] %c' + eventName,
    'color: pink; font-weight: bold;',
    'color: hotpink;',
    data,
  );
};

export const AppSelectPlanB = () => {
  // 店铺列表（从事件获取，初始为空）
  const [merchantList, setMerchantList] = useState<MerchantOption[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<string>('');
  // 菜单完整信息
  const [menuInfo, setMenuInfo] = useState<ExtendedMenu | null>(null);
  // 是否允许展示店铺列表（由 canShowMerchantList 事件控制）
  // todo
  const [canShowList, setCanShowList] = useState<boolean>(false);
  const app = usePortalStore(state => state.app);
  const menu = usePortalStore(state => state.menu);
  const location = useLocation();

  // 根据 menu 获取菜单完整信息
  useEffect(() => {
    if (!app || !menu) {
      setMenuInfo(null);
      return;
    }
    parseApp(app).then(appInfo => {
      if (appInfo?.menus) {
        // 查找匹配的菜单项
        const matched = appInfo.menus.find(m => {
          // 直接匹配一级菜单
          if (m.path === menu) return true;
          // 匹配二级菜单（一级为 /）
          if (m.path === '/' && m.submenus?.some(s => s.path === menu)) {
            return true;
          }
          return false;
        });
        setMenuInfo(matched || null);
        // logEvent('menuInfoMatched', matched);
      }
    });
  }, [app, menu]);

  // 监听商铺完整快照、兼容增量更新和显示状态事件
  useEffect(() => {
    interface MerchantEventData {
      id: string | number;
      name: string;
      isDefault: boolean;
    }

    interface MerchantListEventData {
      data?: MerchantEventData[];
      currentMerchantId?: string | number | null;
    }

    const normalizeMerchantId = (id: string | number) => String(id);

    const selectMerchant = (
      options: MerchantOption[],
      preferredId: string | number | null | undefined,
      previousId: string,
    ) => {
      const preferredValue = preferredId == null ? '' : normalizeMerchantId(preferredId);
      if (preferredValue && options.some(item => item.value === preferredValue)) {
        return preferredValue;
      }
      if (previousId && options.some(item => item.value === previousId)) {
        return previousId;
      }
      return options.find(item => item.isDefault)?.value || options[0]?.value || '';
    };

    const handleMerchantList = (e: CustomEvent<MerchantListEventData>) => {
      const rawData = Array.isArray(e.detail?.data) ? e.detail.data : [];
      logEvent('基座拿到店铺列表 merchantList', e);

      const seen = new Set<string>();
      const options: MerchantOption[] = rawData.reduce<MerchantOption[]>((result, item) => {
        const value = normalizeMerchantId(item.id);
        if (seen.has(value)) return result;
        seen.add(value);
        result.push({
          value,
          text: item.name,
          shownName: item.name,
          isDefault: item.isDefault,
        });
        return result;
      }, []);

      setMerchantList(options);
      setSelectedMerchant(previousId =>
        selectMerchant(options, e.detail?.currentMerchantId, previousId),
      );
    };

    // operatingMerchant 保留用于兼容旧子应用，完整 merchantList 快照是最终数据源。
    const handleOperatingMerchant = (
      e: CustomEvent<{ type: 'add' | 'edit'; data: MerchantEventData }>,
    ) => {
      const { type, data: rawData } = e.detail || {};
      logEvent('operatingMerchant', e);
      if (!rawData) return;

      const data: MerchantOption = {
        value: normalizeMerchantId(rawData.id),
        text: rawData.name,
        isDefault: rawData.isDefault,
      };

      if (type === 'add') {
        setMerchantList(prev => [data, ...prev.filter(item => item.value !== data.value)]);
        setSelectedMerchant(data.value);
      } else if (type === 'edit') {
        setMerchantList(prev =>
          prev.map(item => (item.value === data.value ? data : item)),
        );
      }
    };

    const handleCanShowMerchantList = (e: Event) => {
      const detail = (e as CustomEvent<{ visible?: boolean }>)?.detail;
      logEvent('基座拿到 canShowMerchantList 事件', detail);
      setCanShowList(detail?.visible !== false);
    };
    const handleHideMerchantList = () => {
      setCanShowList(false);
    };

    document.body.addEventListener('merchantList', handleMerchantList as EventListener);
    document.body.addEventListener('operatingMerchant', handleOperatingMerchant as EventListener);
    document.body.addEventListener('canShowMerchantList', handleCanShowMerchantList);
    document.body.addEventListener('hideMerchantList', handleHideMerchantList);

    // 监听器就绪后请求子应用重发完整壳层快照，覆盖错过瞬时事件的情况。
    document.body.dispatchEvent(new CustomEvent('deskbuddyShellReady'));

    return () => {
      document.body.removeEventListener('merchantList', handleMerchantList as EventListener);
      document.body.removeEventListener('operatingMerchant', handleOperatingMerchant as EventListener);
      document.body.removeEventListener('canShowMerchantList', handleCanShowMerchantList);
      document.body.removeEventListener('hideMerchantList', handleHideMerchantList);
    };
  }, [location.pathname]);

  // 店铺切换时发送事件
  const handleMerchantChange = useCallback((value: string) => {
    setSelectedMerchant(value);
    const switchMerchantEvent = new CustomEvent('switchMerchant', {
      detail: { value },
    });
    logEvent('基座发送 switchMerchant 事件', { id: value });
    document.body?.dispatchEvent(switchMerchantEvent);
  }, []);

  // 点击新增店铺发送事件
  const handleAddMerchant = useCallback(() => {
    const addMerchantEvent = new CustomEvent('addMerchant');
    logEvent('基座发送addMerchant事件', null);
    document.body?.dispatchEvent(addMerchantEvent);
  }, []);

  // 计算是否显示 Select
  const shouldShowSelect = useMemo(() => {
    // canShowMerchantList 事件控制的 canShowList 为 false 时不显示
    if (!canShowList) {
      return false;
    }
    // menuInfo 的 hideTopMerchantList 为 true 时不显示
    if (menuInfo?.hideTopMerchantList) {
      return false;
    }
    return true;
  }, [canShowList, menuInfo?.hideTopMerchantList]);

  return (
    <>
      {shouldShowSelect ? (
        <Select
          className="is-v2-nav"
          overlayClassName="is-v2-nav merchant-select-overlay"
          appearance="button"
          placement="bottom-end"
          // placeholder={t('Loading...')}
          options={merchantList}
          value={selectedMerchant}
          onChange={value => handleMerchantChange(value)}
          searchable
          style={{ minWidth: 250 }}
          footer={
            <a onClick={handleAddMerchant}>
              <AddCircleIcon color="#1C66E5" size={16} />
              Add Another Business
            </a>
          }
        />
      ) : null}
    </>
  );
};
